import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialogClose,
    MatDialogRef
} from '@angular/material/dialog';
import {
    ChipSelectOption,
    IconTheme,
    TapestryButtonModule,
    TapestryChipSelectComponent,
    TapestryIconButtonModule
} from '@predictiveindex/tapestry';
import { catchError, from, of, tap } from 'rxjs';
import {
    ClassificationType,
    DemoAccountUpdatePayload,
    FilterInfo,
    FilterType,
    SellerToolsAccountTableData,
    User
} from '../../../../../core-data-model';
import {
    DemoAccountsRepositoryService,
    UserApi
} from '../../../../../core-services';
import { InitDataService, Logger } from '../../../../../system-services';
import { TranslationsModule } from '../../../../../translations';

@Component({
    selector: 'add-third-party-users-modal',
    imports: [
        MatDialogClose,
        TapestryButtonModule,
        TapestryIconButtonModule,
        TranslationsModule,
        ReactiveFormsModule,
        TapestryChipSelectComponent
    ],
    templateUrl: './add-third-party-users-modal.component.html',
    styleUrl: './add-third-party-users-modal.component.less'
})
export class AddThirdPartyUsersModalComponent implements OnInit {
    dialogData: SellerToolsAccountTableData = inject(MAT_DIALOG_DATA);
    thirdPartyUserModalBodyKey =
        this.dialogData.demoDataClassificationType ===
        ClassificationType.DynamicDemo
            ? 'client-core.seller-experience.third-party-users.modal.demo-account-description'
            : 'client-core.seller-experience.third-party-users.modal.workshop-account-description';

    saving: boolean;
    loadingThirdPartyUsers: boolean;
    thirdPartyUserOptions: ChipSelectOption[] = [];
    form: FormGroup<{
        thirdPartyUsers: FormControl<ChipSelectOption[]>;
    }>;

    protected readonly IconTheme = IconTheme;

    private readonly fb = inject(FormBuilder);
    private readonly dialogRef = inject(
        MatDialogRef<AddThirdPartyUsersModalComponent>
    );
    private readonly initData = inject(InitDataService);
    private readonly demoAccountsRepository = inject(
        DemoAccountsRepositoryService
    );
    private readonly userApi = inject(UserApi);
    private readonly logger = inject(Logger);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit() {
        this.form = this.fb.group({
            thirdPartyUsers: this.fb.control<ChipSelectOption[]>(
                [],
                [this.minThirdPartyUser(1)]
            )
        });
    }

    thirdPartyUserFilterChange(filterTerm?: string) {
        if (filterTerm) {
            this.fetchLinkOwners(filterTerm).subscribe(() => {
                this.loadingThirdPartyUsers = false;
            });
        }
    }

    cancel() {
        this.dialogRef.close();
    }

    save() {
        if (this.form.invalid) return;

        this.saving = true;
        const payload = this.buildPayload();

        this.demoAccountsRepository
            .updateDemoAccount(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.saving = false;
                this.dialogRef.close();
            });
    }

    private buildPayload(): DemoAccountUpdatePayload {
        return {
            companyGuid: this.dialogData.companyGuid,
            prospectData: [],
            requestingUserId: this.initData.profile.userGuid,
            thirdPartyAccessIds: this.form.value.thirdPartyUsers.map(
                (x) => x.value
            )
        };
    }

    private fetchLinkOwners(searchTerm?: string) {
        this.loadingThirdPartyUsers = true;

        return this.fetchUsers(searchTerm)
            .pipe(
                catchError((error) => {
                    this.logger.logError('Error fetching users:', error);
                    return of([] as User[]);
                })
            )
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((users) => {
                    this.thirdPartyUserOptions = users.map<ChipSelectOption>(
                        (u) => ({
                            displayName: `${u.firstName} ${u.lastName}`.trim(),
                            value: u.id
                        })
                    );
                })
            );
    }

    private fetchUsers(searchTerm?: string) {
        let filters: FilterInfo[] = null;

        if (searchTerm) {
            filters = [
                new FilterInfo({
                    type: FilterType.Search,
                    parameters: { term: searchTerm.trim().toLowerCase() }
                })
            ];
        }

        return from(this.userApi.getUsers(50, filters, 'all'));
    }

    private readonly minThirdPartyUser =
        (min: number): ValidatorFn =>
        (
            control: AbstractControl<ChipSelectOption[]>
        ): ValidationErrors | null => {
            if (
                !control.value ||
                (Array.isArray(control.value) && control.value.length < min)
            ) {
                return { minThirdPartyUser: true };
            }

            return null;
        };
}
